// Parser + evaluator for PCI's GlobalCriteria string format
// (CPCICriteriaClause / CPCICriteriaLeaf, ported from CPCICriteriaClause.cls,
// CPCICriteriaLeaf.cls and CPCIDictionary.cls in the VB6 source).
//
// A criteria string is either a clause (a boolean tree of child clauses/leaves)
// or a single leaf (one field comparison). Clauses store their children in a
// length-prefixed "CPCIDictionary" string: `key^length^value` repeated, where
// `value` is read as exactly `length` characters (never delimiter-split,
// since a value can itself contain "^"). A value starting with `||OBJ<Type>||`
// is a nested serialized object; anything else is a literal string.

export const CLAUSE_TYPE = { AND: 1, OR: 2, NOT: 3 };

export const CRITERIA_TYPE = {
  EQUAL_TO: 1,
  NOT_EQUAL_TO: 2,
  SOMETHING: 3, // field IS NOT NULL / has a value
  NOTHING: 4, // field IS NULL
  LESS_THAN: 5,
  GREATER_THAN: 6,
  LESS_THAN_OR_EQUAL: 7,
  GREATER_THAN_OR_EQUAL: 8,
  BETWEEN: 9,
};

// Parses a length-prefixed CPCIDictionary string into [{ key, value|node }, ...].
const parseDictionaryString = (str) => {
  const entries = [];
  let pos = 0;
  while (pos < str.length) {
    const keyEnd = str.indexOf("^", pos);
    if (keyEnd === -1) break;
    const key = str.slice(pos, keyEnd);

    const lenEnd = str.indexOf("^", keyEnd + 1);
    if (lenEnd === -1) break;
    const length = parseInt(str.slice(keyEnd + 1, lenEnd), 10);

    const valueStart = lenEnd + 1;
    const value = str.slice(valueStart, valueStart + length);
    pos = valueStart + length;

    if (value.startsWith("||OBJ")) {
      // ||OBJ<TypeNameOrID>||<restOfSerializedObject>
      const typeEnd = value.indexOf("||", 5);
      const rest = value.slice(typeEnd + 2);
      entries.push({ key, node: parseNode(rest) });
    } else {
      entries.push({ key, value });
    }
  }
  return entries;
};

const parseNode = (str) => {
  if (!str) return null;
  if (str.startsWith("CPCICriteriaClause@@")) return parseClause(str);
  if (str.startsWith("CPCICriteriaLeaf@@")) return parseLeaf(str);
  return null;
};

// CPCICriteriaClause@@version:criteriaId:internalId:clauseType:fieldSource:<childrenDictString>
const parseClause = (str) => {
  const afterClass = str.slice(str.indexOf("@@") + 2);

  const colonParts = [];
  let pos = 0;
  for (let i = 0; i < 4; i++) {
    const next = afterClass.indexOf(":", pos);
    colonParts.push(afterClass.slice(pos, next));
    pos = next + 1;
  }
  const [version, criteriaId, internalId, clauseType] = colonParts;

  const fieldSourceEnd = afterClass.indexOf(":", pos);
  const fieldSource = afterClass.slice(pos, fieldSourceEnd);
  const rest = afterClass.slice(fieldSourceEnd + 1);

  const children = parseDictionaryString(rest)
    .map((e) => e.node)
    .filter(Boolean);

  return {
    type: "clause",
    version: parseInt(version, 10),
    criteriaId: parseInt(criteriaId, 10),
    internalId: parseInt(internalId, 10),
    clauseType: parseInt(clauseType, 10),
    fieldSource,
    children,
  };
};

// CPCICriteriaLeaf@@version@@criteriaId@@fieldName@@criteriaType@@searchType@@
//   fieldSource@@promptType@@defaultDateRange@@[dropDownSource if v>=2]@@
//   [variableName@@promptText if v>=5]@@[justStoreInVariable if v>=6]@@
//   [internalId if v>=3]@@[matchAllRecords if v>=4]@@[unionLevel if v>=7]@@valueSetRaw
const parseLeaf = (str) => {
  const fields = str.split("@@");
  const version = parseInt(fields[1], 10);

  let idx = 9;
  let dropDownSource = "";
  if (version >= 2) {
    dropDownSource = fields[idx];
    idx += 1;
  }
  let variableName = "";
  let promptText = "";
  if (version >= 5) {
    variableName = fields[idx];
    idx += 1;
    promptText = fields[idx];
    idx += 1;
  }
  let justStoreInVariable = false;
  if (version >= 6) {
    justStoreInVariable = fields[idx] === "True";
    idx += 1;
  }
  let internalId = null;
  if (version >= 3) {
    internalId = fields[idx];
    idx += 1;
  }
  let matchAllRecords = false;
  if (version >= 4) {
    matchAllRecords = fields[idx] === "True";
    idx += 1;
  }
  let unionLevel = null;
  if (version >= 7) {
    unionLevel = fields[idx];
    idx += 1;
  }

  // Whatever's left (rejoined, in case the value itself contained "@@") is
  // the ValueSet — itself a nested CPCIDictionary whose keys are the actual
  // comparison values (e.g. "TRUE", "1", "@MYAGENCY").
  const valueSetRaw = fields.slice(idx).join("@@");
  const valueSet = parseDictionaryString(valueSetRaw).map((e) => e.key);

  return {
    type: "leaf",
    version,
    criteriaId: parseInt(fields[2], 10),
    fieldName: fields[3],
    criteriaType: parseInt(fields[4], 10),
    searchType: fields[5],
    fieldSource: fields[6],
    promptType: fields[7],
    defaultDateRange: fields[8],
    dropDownSource,
    variableName,
    promptText,
    justStoreInVariable,
    internalId,
    matchAllRecords,
    unionLevel,
    valueSet,
  };
};

// Parses a full criteriaString (clause or bare leaf) into a tree.
export const parseCriteriaString = (criteriaString) => {
  if (!criteriaString) return null;
  try {
    return parseNode(criteriaString);
  } catch {
    return null;
  }
};

// Resolves a comparison value against a record/variable context. Values
// starting with "@" are variable substitutions (e.g. "@MYAGENCY" -> the
// current user's agency), resolved from `context`.
const resolveValue = (token, context) => {
  if (typeof token === "string" && token.startsWith("@")) {
    return context?.[token.slice(1)];
  }
  return token;
};

const isEmpty = (value) => value === null || value === undefined || value === "";

// GlobalCriteria field names are the legacy PascalCase/underscore DB column
// names (e.g. "Sealed", "Housing_Agency"), but API responses are typically
// camelCase (e.g. "sealed", "housingAgency"). Try an exact match first, then
// a camelCase-folded match, then fall back to a case-insensitive scan of the
// record's own keys so callers don't have to remap every field themselves.
const getFieldValue = (record, fieldName) => {
  if (!record || !fieldName) return undefined;
  if (fieldName in record) return record[fieldName];

  const camelCased = fieldName
    .replace(/_([a-zA-Z0-9])/g, (_m, c) => c.toUpperCase())
    .replace(/^[A-Z]/, (c) => c.toLowerCase());
  if (camelCased in record) return record[camelCased];

  const lowerTarget = fieldName.replace(/_/g, "").toLowerCase();
  const matchKey = Object.keys(record).find(
    (k) => k.replace(/_/g, "").toLowerCase() === lowerTarget,
  );
  return matchKey ? record[matchKey] : undefined;
};

// Evaluates a parsed criteria tree against a record. Returns true if the
// record satisfies the criteria (or the criteria is empty/unrestricted).
// `context` supplies values for "@"-prefixed variable tokens (e.g.
// { MYAGENCY: currentUser.organization.agency }).
// NOTE: fieldSource paths that reference a parent/joined table (e.g.
// "..\RefLists") aren't resolved beyond a flat field lookup on `record` —
// this handles the common single-table case, not multi-level joins.
export const evaluateCriteriaNode = (node, record, context = {}) => {
  if (!node) return true;

  if (node.type === "clause") {
    // Matches CPCICriteriaClause::IPCICriteria_Satisfied's default: an empty
    // AND or NOT is vacuously satisfied, an empty OR is not.
    if (!node.children.length) return node.clauseType !== CLAUSE_TYPE.OR;
    const results = node.children.map((child) =>
      evaluateCriteriaNode(child, record, context),
    );
    switch (node.clauseType) {
      case CLAUSE_TYPE.OR:
        return results.some(Boolean);
      // NOT is a NOR: satisfied only when every child fails (one success
      // anywhere fails the whole NOT), not merely "not all succeeded".
      case CLAUSE_TYPE.NOT:
        return !results.some(Boolean);
      case CLAUSE_TYPE.AND:
      default:
        return results.every(Boolean);
    }
  }

  // Leaf
  if (node.matchAllRecords || !node.fieldName) return true;

  const fieldValue = getFieldValue(record, node.fieldName);
  const compareValues = (node.valueSet || []).map((v) =>
    resolveValue(v, context),
  );

  switch (node.criteriaType) {
    case CRITERIA_TYPE.EQUAL_TO:
      return compareValues.some(
        (v) => String(fieldValue).toLowerCase() === String(v).toLowerCase(),
      );
    case CRITERIA_TYPE.NOT_EQUAL_TO:
      return compareValues.every(
        (v) => String(fieldValue).toLowerCase() !== String(v).toLowerCase(),
      );
    case CRITERIA_TYPE.SOMETHING:
      return !isEmpty(fieldValue);
    case CRITERIA_TYPE.NOTHING:
      return isEmpty(fieldValue);
    case CRITERIA_TYPE.LESS_THAN:
      return compareValues.some((v) => fieldValue < v);
    case CRITERIA_TYPE.GREATER_THAN:
      return compareValues.some((v) => fieldValue > v);
    case CRITERIA_TYPE.LESS_THAN_OR_EQUAL:
      return compareValues.some((v) => fieldValue <= v);
    case CRITERIA_TYPE.GREATER_THAN_OR_EQUAL:
      return compareValues.some((v) => fieldValue >= v);
    case CRITERIA_TYPE.BETWEEN:
      return (
        compareValues.length >= 2 &&
        fieldValue >= compareValues[0] &&
        fieldValue <= compareValues[1]
      );
    default:
      return true;
  }
};
